import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { formatPercent } from '@/lib/utils';
import {
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Rocket,
  XCircle,
} from 'lucide-react';
import { getV2Progress } from '@/api/v2-deployment';
import { getSchool } from '@/api/schools';
import { useAuth } from '@/contexts/AuthContext';
import { V2DeployDialog } from '@/components/schools/V2DeployDialog';
import type { V2DeploymentInfo, V2SetupStatus, V2DeployRequest } from '@/types/v2-deployment';
import type { School } from '@/types/school';
import { v2ModuleLabel } from './modulePerimeter';

interface V2TargetStatusCardProps {
  schoolCode: string;
  /** Appelée quand l'état "prêt pour migration" change. */
  onReadyChange?: (ready: boolean) => void;
  /** Poll auto quand le déploiement est en cours. */
  autoRefresh?: boolean;
  refreshInterval?: number;
  /** Retire la bordure/ombre extérieures (utile quand le composant est imbriqué dans une carte). */
  borderless?: boolean;
  /**
   * Codes des modules V2 souscrits par l'école (périmètre de migration).
   * Affichés en chips dans le bloc « prêt ». `null`/`undefined`/vide ⇒ masqué.
   */
  activeV2Modules?: string[] | null;
  /**
   * Signal incrémenté par le parent pour relancer un déploiement V2 automatique
   * (ex. après un « recommencer de zéro »). À chaque changement de valeur, le
   * dialog de progression s'ouvre et le déploiement démarre seul, sans redemander
   * l'offre : l'abonnement (offre/modules/options) est conservé côté back au reset,
   * donc une config vide suffit à le reprendre.
   */
  autoDeploySignal?: number;
  /**
   * Appelée à la fermeture du dialog de déploiement V2. Permet au parent de
   * rafraîchir ses données (ex. le projet de migration, dont le v2TenantId et
   * la config d'années ont changé après un « recommencer de zéro » + redéploiement).
   */
  onDeployDialogClose?: () => void;
}

const statusConfig: Record<V2SetupStatus, {
  label: string;
  badge: 'success' | 'warning' | 'default' | 'secondary';
  icon: typeof CheckCircle2;
  color: string;
}> = {
  not_started: {
    label: 'Non démarré',
    badge: 'warning',
    icon: AlertTriangle,
    color: 'text-orange-500',
  },
  in_progress: {
    label: 'Déploiement en cours',
    badge: 'default',
    icon: Loader2,
    color: 'text-blue-500',
  },
  completed: {
    label: 'Prêt',
    badge: 'success',
    icon: CheckCircle2,
    color: 'text-green-500',
  },
  failed: {
    label: 'Échec',
    badge: 'warning',
    icon: XCircle,
    color: 'text-red-500',
  },
};

/**
 * Affiche l'état du tenant V2 cible pour la migration.
 *
 * La cible V2 est l'environnement déployé par l'ADM via SchoolDeployment
 * (pas configurée directement dans la migration). Cette carte :
 *  - lit l'état du déploiement V2 via /api/schools/{code}/v2-progress
 *  - invite à déployer V2 si ce n'est pas fait (lien vers fiche école)
 *  - affiche la progression si un déploiement est en cours (polling auto)
 *  - affiche les infos du tenant (groupId, tenantId) si prêt
 *
 * La migration ne peut démarrer que si `ready === true` (setup completed).
 */
export function V2TargetStatusCard({
  schoolCode,
  onReadyChange,
  autoRefresh = true,
  refreshInterval = 5000,
  borderless = false,
  activeV2Modules,
  autoDeploySignal,
  onDeployDialogClose,
}: V2TargetStatusCardProps) {
  const location = useLocation();
  const { hasPermission } = useAuth();
  const canDeploy = hasPermission('action.v2.deploy');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<V2DeploymentInfo | null>(null);

  // Le déploiement initial passe par le wizard « Passage en V2 » (qualification
  // des données V1 : infos, établissements, branding) avant création du tenant
  const upgradeWizardPath = `/schools/${schoolCode}/upgrade-v2?returnTo=${encodeURIComponent(
    location.pathname
  )}`;

  // Déploiement V2 in situ : la fiche école complète est chargée à la demande
  // (whiteLabelEntityId / hasV1 ne sont pas dans le projet de migration).
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [deploySchool, setDeploySchool] = useState<School | null>(null);
  const [openingDeploy, setOpeningDeploy] = useState(false);
  // Config passée au dialog : définie (même vide) ⇒ déploiement automatique
  // sans formulaire d'offre. `undefined` ⇒ le dialog affiche le formulaire.
  const [autoDeployConfig, setAutoDeployConfig] = useState<V2DeployRequest | undefined>(undefined);

  const onReadyChangeRef = useRef(onReadyChange);
  useEffect(() => {
    onReadyChangeRef.current = onReadyChange;
  }, [onReadyChange]);

  const fetchProgress = useCallback(async () => {
    try {
      const result = await getV2Progress(schoolCode);
      setData(result);
      setError(null);
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        // Pas encore déployé : on considère comme "non déployé"
        setData({ deployed: false } as V2DeploymentInfo);
        setError(null);
      } else if (axiosError.response?.status === 403) {
        setError('Statut V2 non disponible avec vos permissions (action.v2.progress requise)');
      } else {
        setError((err as Error).message || 'Impossible de récupérer le statut V2');
      }
    } finally {
      setLoading(false);
    }
  }, [schoolCode]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const handleOpenDeploy = async () => {
    // Ouverture manuelle (« Corriger ») : formulaire d'offre, pas d'auto-deploy.
    setAutoDeployConfig(undefined);
    if (deploySchool) {
      setDeployDialogOpen(true);
      return;
    }
    setOpeningDeploy(true);
    try {
      const school = await getSchool(schoolCode);
      setDeploySchool(school);
      setDeployDialogOpen(true);
    } catch {
      setError("Impossible de charger l'école pour le déploiement");
    } finally {
      setOpeningDeploy(false);
    }
  };

  const handleDeployDialogChange = (open: boolean) => {
    setDeployDialogOpen(open);
    if (!open) {
      // À la fermeture : oublie la config auto (une réouverture « Corriger »
      // doit repartir sur le formulaire) et rafraîchit le statut. Le parent est
      // aussi prévenu pour recharger ses propres données (le déploiement a pu
      // changer le tenant, donc invalider années V2, tenant id du projet, etc.).
      setAutoDeployConfig(undefined);
      fetchProgress();
      onDeployDialogClose?.();
    }
  };

  // Redéploiement automatique déclenché par le parent (« recommencer de zéro ») :
  // on charge l'école puis on ouvre le dialog en mode auto-deploy. La config est
  // vide car l'abonnement (offre/modules/options) est conservé côté back au reset.
  const autoDeploySignalRef = useRef(autoDeploySignal);
  useEffect(() => {
    if (autoDeploySignal === undefined || autoDeploySignal === autoDeploySignalRef.current) {
      return;
    }
    autoDeploySignalRef.current = autoDeploySignal;
    let cancelled = false;
    (async () => {
      setOpeningDeploy(true);
      setError(null);
      try {
        const school = deploySchool ?? (await getSchool(schoolCode));
        if (cancelled) return;
        setDeploySchool(school);
        setAutoDeployConfig({}); // config vide ⇒ le back réutilise l'abonnement existant
        setDeployDialogOpen(true);
      } catch {
        if (!cancelled) setError("Impossible de charger l'école pour le redéploiement");
      } finally {
        if (!cancelled) setOpeningDeploy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [autoDeploySignal, schoolCode, deploySchool]);

  // État dérivé : prêt si deployment existe et setup completed
  const currentStatus: V2SetupStatus = data?.progress?.status ?? data?.localStatus ?? 'not_started';
  const ready = Boolean(data?.deployed) && currentStatus === 'completed';

  useEffect(() => {
    onReadyChangeRef.current?.(ready);
  }, [ready]);

  // Polling auto quand déploiement en cours
  useEffect(() => {
    if (!autoRefresh) return;
    const shouldPoll = !ready && !error && (
      !data?.deployed || currentStatus === 'not_started' || currentStatus === 'in_progress'
    );
    if (!shouldPoll) return;
    const interval = setInterval(fetchProgress, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, ready, error, data?.deployed, currentStatus, fetchProgress, refreshInterval]);

  const cfg = statusConfig[currentStatus];
  const StatusIcon = cfg.icon;

  const statusBadge = loading ? (
    <Badge variant="secondary" className="gap-1">
      <Loader2 className="h-3 w-3 animate-spin" /> Chargement
    </Badge>
  ) : !data?.deployed ? (
    <Badge variant="warning" className="gap-1">
      <AlertTriangle className="h-3 w-3" /> Non déployé
    </Badge>
  ) : (
    <Badge variant={cfg.badge} className="gap-1">
      <StatusIcon className={`h-3 w-3 ${currentStatus === 'in_progress' ? 'animate-spin' : ''}`} />
      {cfg.label}
    </Badge>
  );

  return (
    <div
      className={
        borderless
          ? 'h-full'
          : 'rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 shadow-sm h-full'
      }
    >
      <div className="px-4 py-3 flex items-center gap-2.5 border-b border-gray-100 dark:border-slate-700">
        <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">Environnement V2</h3>
        {statusBadge}
        <Button variant="ghost" size="icon-sm" asChild className="ml-auto" title="Fiche école">
          <Link to={`/schools/${schoolCode}`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>

      <div className="px-4 py-3 text-sm">
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Vérification…
          </div>
        ) : error ? (
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-red-600 truncate">{error}</div>
            <Button variant="outline" size="sm" onClick={fetchProgress}>Réessayer</Button>
          </div>
        ) : !data?.deployed ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2 text-orange-700 dark:text-orange-300 min-w-0">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span className="text-xs">Aucun environnement V2 déployé.</span>
            </div>
            {canDeploy && (
              <Button size="sm" asChild>
                <Link to={upgradeWizardPath}>
                  <Rocket className="h-3.5 w-3.5" />
                  Déployer
                </Link>
              </Button>
            )}
          </div>
        ) : currentStatus === 'in_progress' ? (
          <div className="space-y-1.5">
            {data.progress?.progress_percentage !== undefined && (
              <>
                <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                  <span>{data.progress.completed_steps ?? 0} / {data.progress.total_steps ?? 0} étapes</span>
                  <span className="font-medium">{formatPercent(data.progress.progress_percentage)}%</span>
                </div>
                <Progress value={data.progress.progress_percentage} className="h-1.5" />
              </>
            )}
            <p className="text-xs text-gray-500">Déploiement en cours, mise à jour automatique.</p>
          </div>
        ) : currentStatus === 'failed' ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2 text-red-700 dark:text-red-300 min-w-0">
              <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span className="text-xs truncate">
                {data.progress?.error || 'Déploiement V2 en échec.'}
              </span>
            </div>
            {canDeploy && (
              <Button size="sm" onClick={handleOpenDeploy} disabled={openingDeploy}>
                {openingDeploy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Corriger
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-2 min-w-0">
              <span className="text-xs uppercase tracking-wide text-gray-500 shrink-0">Tenant</span>
              <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {data.tenant?.name ?? '—'}
                {data.tenant?.code && (
                  <span className="ml-1.5 text-xs font-mono text-gray-500">{data.tenant.code}</span>
                )}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2 min-w-0">
              <span className="text-xs uppercase tracking-wide text-gray-500 shrink-0">Group</span>
              <span className="text-right min-w-0 truncate" title={data.tenantGroupId ?? ''}>
                {data.tenantGroupName && (
                  <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
                    {data.tenantGroupName}
                  </span>
                )}
                <span className="ml-1.5 font-mono text-xs text-gray-500 dark:text-gray-400">
                  {data.tenantGroupId ?? '—'}
                </span>
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2 min-w-0">
              <span className="text-xs uppercase tracking-wide text-gray-500 shrink-0">ID</span>
              <span className="font-mono text-xs text-gray-600 dark:text-gray-400 truncate" title={data.tenantId ?? ''}>
                {data.tenantId ?? '—'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-green-600 pt-1 border-t border-gray-100 dark:border-slate-700 mt-2">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Migration possible.
            </div>
            {activeV2Modules && activeV2Modules.length > 0 && (
              <div className="pt-2 border-t border-gray-100 dark:border-slate-700 mt-2">
                <p className="mb-1.5 text-xs uppercase tracking-wide text-gray-500">
                  Modules activés
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {activeV2Modules.map((code) => (
                    <span
                      key={code}
                      className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300"
                    >
                      {v2ModuleLabel(code)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {deploySchool && (
        <V2DeployDialog
          open={deployDialogOpen}
          onOpenChange={handleDeployDialogChange}
          schoolCode={schoolCode}
          schoolName={deploySchool.name}
          hasV1={deploySchool.hasV1 || false}
          deployConfig={autoDeployConfig}
          onSuccess={fetchProgress}
        />
      )}
    </div>
  );
}
